/**
 * WidgetPanel: 単一CV系（PreBookingForm/SurveyPopup）・複数CV系（MultiCvAppointmentList）で
 * 共有する外枠コンポーネント。design-mockups/*.html（2026-08-06 mono.ipros.com実測トークン版）準拠。
 *
 * - 単一CV系（配置=bottom-left）: 左下固定パネル。
 * - 複数CV系（配置=center-modal）: 画面中央モーダル、背景暗転。
 * - 未ログイン時のログインゲート（BR-U6-1/BR-U6-2統一表現）: コンテンツ領域をブラーし、
 *   半透明の暗い背景＋白カード（メッセージ＋「ログイン / 会員登録する」＋「閉じる」）を重畳する。
 */

import { createElement } from "../core/shadowDomUtil";
import {
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  BORDER_DEFAULT,
  SURFACE_SUBTLE,
  BRAND_PRIMARY,
  RADIUS_DEFAULT,
  SHADOW_MODAL,
  BACKDROP_MODAL,
  BACKDROP_LOGIN_GATE,
  FONT_FAMILY,
} from "./designTokens";
import { buildLoginRedirectUrl, buildRegisterRedirectUrl } from "./loginPromptOverlayUi";
import { trackLoginPrompt } from "../w3-tracking/trackPageEvent";
import type { LoginPromptReason, PageEventPayload } from "../types";

export type WidgetPanelPosition = "bottom-left" | "center-modal";

export const WIDGET_PANEL_STYLES = `
:host { all: initial; }
* { box-sizing: border-box; }
.spica-widget-backdrop {
  position: fixed; inset: 0; z-index: 2147483647;
  background: ${BACKDROP_MODAL};
  display: flex; align-items: center; justify-content: center;
  padding: 40px 24px;
}
.spica-widget-bottom-left {
  position: fixed; left: 24px; bottom: 24px; z-index: 2147483647;
  max-width: min(700px, calc(100vw - 48px)); width: max-content;
}
.spica-widget-panel {
  position: relative; box-sizing: border-box;
  width: 100%; max-width: 900px;
  max-height: calc(100vh - 80px); overflow-y: auto;
  background: ${SURFACE_SUBTLE};
  border-radius: ${RADIUS_DEFAULT};
  box-shadow: ${SHADOW_MODAL};
  padding: 34px 40px 28px;
  font-family: ${FONT_FAMILY};
  color: ${TEXT_PRIMARY};
}
.spica-widget-bottom-left .spica-widget-panel {
  max-width: none;
  max-height: calc(100vh - 48px);
}
.spica-widget-header {
  position: sticky; top: -34px; z-index: 2;
  margin: -34px -40px 28px; padding: 20px 68px 18px 40px;
  background: ${SURFACE_SUBTLE}; border-bottom: 1px solid ${BORDER_DEFAULT};
}
.spica-widget-close {
  position: absolute; top: 21px; right: 40px; border: none; background: transparent;
  font-size: 20px; color: ${TEXT_SECONDARY}; cursor: pointer; line-height: 1;
}
.spica-widget-close:hover { color: ${TEXT_PRIMARY}; }
.spica-widget-close:focus-visible { outline: 2px solid ${TEXT_PRIMARY}; outline-offset: 2px; border-radius: 3px; }
/* 縮小トグルボタン（×の代替。展開時=下向きv／縮小時=上向き∧。アイコンはCSSで出し分ける）。 */
.spica-widget-minimize-toggle {
  position: absolute; top: 17px; right: 36px; width: 28px; height: 28px;
  display: inline-flex; align-items: center; justify-content: center;
  border: none; background: transparent; color: ${TEXT_SECONDARY};
  cursor: pointer; padding: 0; line-height: 1;
}
.spica-widget-minimize-toggle:hover { color: ${TEXT_PRIMARY}; }
.spica-widget-minimize-toggle:focus-visible { outline: 2px solid ${TEXT_PRIMARY}; outline-offset: 2px; border-radius: 3px; }
.spica-widget-minimize-toggle svg { width: 18px; height: 18px; display: block; }
/* 展開時は下向きv（collapse-icon）を表示、上向き∧（expand-icon）を隠す。縮小時は反転。 */
.spica-widget-minimize-toggle .spica-widget-toggle-expand-icon { display: none; }
.is-minimized .spica-widget-minimize-toggle .spica-widget-toggle-collapse-icon { display: none; }
.is-minimized .spica-widget-minimize-toggle .spica-widget-toggle-expand-icon { display: inline-flex; }
.spica-widget-lead {
  font-size: 20px; font-weight: 700; line-height: 1.5; margin: 0; color: ${TEXT_PRIMARY}; white-space: nowrap;
}
.spica-widget-content { position: relative; }
.spica-widget-content.is-login-gated { filter: blur(6px); opacity: .85; pointer-events: none; user-select: none; }

/* ===== 縮小状態（is-minimized）: PC・スマホ共通。ヘッダー（タイトル）のみ残し縦のみ縮小。 ===== */
/* コンテンツを隠す（幅は展開時と同じ・縦だけ縮む）。 */
.is-minimized .spica-widget-content { display: none; }
/* 縮小中は重畳中のログインゲート（白カード）も隠す（2026-10-02 第2弾修正、BR-U6-1）。
   ログインゲートは panel 直下で content の兄弟のため、上の content 非表示だけでは隠れない。
   「閉じる」で縮小してもゲートは破棄せず保持する方針のため、縮小中はここで視覚的に隠し、
   再展開（is-minimized 解除）で未ログインゲート＋ブラーがそのまま復活する。 */
.is-minimized .spica-widget-login-gate { display: none; }
/* 縮小時はパネルの下パディングを詰め、ヘッダーのみのコンパクトなバーにする（旧スマホデザイン踏襲）。 */
/* パネル自身が角丸＋overflow:hiddenを持つことで、左端の赤縦線(::before)を角丸内にクリップする（旧ミニ化バーと同じ作り）。 */
/* 背景・影も旧ミニ化バー（白背景＋控えめな影）に合わせる。 */
.is-minimized .spica-widget-panel {
  padding: 0; overflow: hidden; border-radius: ${RADIUS_DEFAULT};
  background: #fff; box-shadow: 0 8px 24px rgba(51, 51, 51, .16);
}
/* 縮小時のヘッダー: sticky解除・マージンとborder下線を除去し、タイトル＋トグルを横並びにしたバーにする。 */
.is-minimized .spica-widget-header {
  position: relative; top: 0; margin: 0; border-bottom: none; background: transparent; cursor: pointer;
  display: flex; align-items: center; min-height: 52px;
  padding: 0 72px 0 20px;
}
/* 左端ブランドカラー縦線（旧スマホバー踏襲。パネルの角丸＋overflow:hiddenでコーナーがクリップされる）。 */
.is-minimized .spica-widget-header::before {
  content: ""; position: absolute; left: 0; top: 0; bottom: 0;
  width: 6px; background: ${BRAND_PRIMARY};
}
/* 縮小時のリード文: 14px。PCでは内容幅（伸ばさず・省略なし）、スマホでは残り幅いっぱい（後述メディアクエリ）。 */
.is-minimized .spica-widget-lead {
  flex: 0 1 auto; min-width: 0; font-size: 14px; line-height: 1.35;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
/* 縮小時のトグルは展開時と同じ右端の絶対配置を維持（初期位置のまま・左へ寄らない）。バー高さに合わせ縦中央へ。
   ヘッダーの右padding(44px)でトグル分の幅を確保するため、パネルのmax-content幅にトグル領域が含まれる。 */
.is-minimized .spica-widget-minimize-toggle {
  top: 50%; transform: translateY(-50%);
}
/* 縮小パネルの幅: PC(デフォルト)は内容(タイトル＋トグル右padding)に追従。スマホは後述メディアクエリで全幅化。 */
.spica-widget-bottom-left.is-minimized { width: max-content; max-width: calc(100vw - 48px); }
/* center-modal の縮小: 背景暗転を解除し背後を操作可能（pass-through）にする。 */
.spica-widget-backdrop.is-minimized {
  background: transparent; pointer-events: none;
  align-items: flex-end; justify-content: flex-start; padding: 24px;
}
/* 左下へ移動したパネル自体はクリック可能に戻す（背後はpass-throughのまま）。PCは内容幅に追従。 */
.spica-widget-backdrop.is-minimized .spica-widget-panel {
  pointer-events: auto; cursor: pointer;
  width: max-content; max-width: calc(100vw - 48px);
}
.spica-widget-login-gate {
  position: absolute; inset: 0; z-index: 3;
  background: ${BACKDROP_LOGIN_GATE};
  display: flex; align-items: center; justify-content: center;
  padding: 24px; border-radius: ${RADIUS_DEFAULT};
}
.spica-widget-login-gate-card {
  width: min(300px, 100%); background: #fff; border-radius: ${RADIUS_DEFAULT};
  padding: 24px; box-shadow: ${SHADOW_MODAL}; text-align: center;
}
.spica-widget-login-gate-message {
  margin: 0 0 16px; color: ${TEXT_PRIMARY}; font-size: 13px; font-weight: 700; line-height: 1.5;
}
.spica-widget-login-gate-proceed {
  display: block; width: 100%; padding: 12px; border: none; border-radius: ${RADIUS_DEFAULT};
  background: ${TEXT_PRIMARY}; color: #fff; font: inherit; font-size: 13px; font-weight: 700; cursor: pointer;
  text-decoration: none; text-align: center; box-sizing: border-box;
}
.spica-widget-login-gate-cancel {
  display: block; width: 100%; margin-top: 8px; padding: 4px; border: none; background: none;
  color: ${TEXT_SECONDARY}; font: inherit; font-size: 12px; cursor: pointer;
}
@media (max-width: 767px) {
  .spica-widget-panel {
    max-height: 85vh; overflow-y: auto;
    padding: 24px 20px 20px;
  }
  .spica-widget-header {
    position: sticky; top: -24px; z-index: 2;
    margin: -24px -20px 20px; padding: 16px 46px 14px 20px;
  }
  .spica-widget-close { top: 16px; right: 20px; }
  .spica-widget-minimize-toggle { top: 12px; right: 16px; }
  .spica-widget-lead { font-size: 16px; white-space: normal; }

  .spica-widget-backdrop { padding: 12px; }

  .spica-widget-bottom-left {
    left: 12px; right: 12px; bottom: 12px;
    max-width: none; width: auto;
  }
  /* スマホ縮小時: bottom-left系は画面横幅いっぱい（PCの内容追従 max-content 指定を上書き）。 */
  .spica-widget-bottom-left.is-minimized { width: auto; max-width: none; }
  /* スマホ縮小時: トグルが right:16px のため右paddingを52pxに詰める（PCの72pxを上書き）。 */
  .is-minimized .spica-widget-header { padding: 0 52px 0 20px; }
  /* スマホ縮小時: リード文は残り幅いっぱいに広げ1行省略（トグルは右端）。 */
  .is-minimized .spica-widget-lead { flex: 1 1 auto; }
  /* center-modal 縮小時のパネルはスマホでも左右余白12pxの左下固定・全幅に。 */
  .spica-widget-backdrop.is-minimized { padding: 12px; }
  .spica-widget-backdrop.is-minimized .spica-widget-panel {
    max-width: none; width: auto;
  }
}
`;

export interface LoginGateOptions {
  /** ログインゲートに表示するメッセージ（改行は\nで表現）。省略時はデフォルト文言。 */
  message?: string;
  /** ログインボタンのラベル。省略時は「ログイン / 会員登録する」。 */
  buttonLabel?: string;
}

export interface WidgetPanelHandle {
  /** WidgetPanelのルート要素（配置によりbackdrop or bottom-leftコンテナ）。 */
  root: HTMLElement;
  /** ヘッダー直下、リード文の後に続くコンテンツ領域。呼び出し元がここへUIを構築する。 */
  content: HTMLElement;
  /** WidgetPanel自体を破棄する（×ボタン・ログインキャンセル等から呼ぶ）。 */
  remove: () => void;
  /**
   * ログインゲートを表示する（BR-U6-1/BR-U6-2）。コンテンツをブラーし、
   * ログインカードを重畳する。optionsでメッセージ・ボタンラベルをカスタマイズ可能。
   *
   * <p><b>「閉じる」ボタンの挙動（BR-U6-14）</b>: ゲートを隠した後、パネルの `minimizable` で分岐する。
   * - `minimizable:true`（本体ウィジェット＝アンケート/アポ打診/複数CV）: ウィジェットを縮小し
   *   `onMinimize`（WIDGET_MINIMIZE）を発火する。**この場合、引数 `onCancel` は無視される**
   *   （完全閉じる導線を持たない方針と一貫。縮小へ統一）。
   * - `minimizable:false`（キャンセルモーダル等のファネル外画面）: 引数 `onCancel` を呼ぶ（従来挙動）。
   *
   * @param onCancel `minimizable:false` 時のみ「閉じる」で呼ばれる。`minimizable:true` では無視される。
   */
  showLoginGate: (loginUrl: string, onCancel?: () => void, options?: LoginGateOptions) => void;
  /** ログインゲートを非表示にする。 */
  hideLoginGate: () => void;
  /** ウィジェットを縮小状態にする（`minimizable:true` 時のみ有効。それ以外はno-op）。 */
  collapse: () => void;
  /** ウィジェットを展開状態に戻す（`minimizable:true` 時のみ有効。それ以外はno-op）。 */
  expand: () => void;
  /** 現在縮小状態かどうかを返す。 */
  isMinimized: () => boolean;
}

export interface WidgetPanelOptions {
  /** 配置。単一CV系="bottom-left"、複数CV系="center-modal"。 */
  position: WidgetPanelPosition;
  /** ヘッダーに表示するリード文。 */
  lead: string;
  /** aria-label（ダイアログ全体の説明）。 */
  ariaLabel: string;
  /**
   * 縮小トグルを有効にするか（2026-09-28追加）。
   * - `true`: ヘッダー右を縮小トグル（v ⇄ ∧）にする。「×＝閉じる」は表示しない（完全閉じる廃止）。
   *   縮小はアイコン押下時のみ発生し、縮小バー全体クリック/Enter/Spaceで展開に戻る。
   *   初期状態は `position` と画面幅で判定（bottom-left系はスマホで縮小・PCで展開、center-modal系は常に展開）。
   * - `false`（デフォルト）: 従来どおりヘッダー右を「×＝閉じる」（`onClose`）にする。
   *   アポイントキャンセルモーダル等、縮小対象外の経路はこちらを使う（挙動不変）。
   */
  minimizable?: boolean;
  /** ×ボタン押下時に呼ばれる（`minimizable:false` 時のみ有効。デフォルトはremove()と同一）。 */
  onClose?: () => void;
  /**
   * 効果分析ログ（BR-AN-14）: ユーザーが縮小トグルでウィジェットを畳んだ時（離脱意思）に呼ばれる
   * （`minimizable:true` 時のみ。初期表示の縮小・外部からの collapse() 呼び出しでは呼ばれない）。
   * 呼び出し元が WIDGET_MINIMIZE を発火する（sessionId/companyRootId 等の分析文脈は呼び出し元が付与）。
   */
  onMinimize?: () => void;
}

const LOGIN_GATE_MESSAGE = "掲載内容について確認・相談するには、\nログインが必要です";

/** スマホ判定のブレークポイント（polaris-mono基準、frontend-components.md準拠）。 */
const MOBILE_MEDIA_QUERY = "(max-width: 767px)";

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * 縮小トグル用のシェブロン線画SVGアイコンを`createElementNS`で安全に構築する
 * （`innerHTML`不使用・NFR-U6-SEC2、絵文字不使用、自作SVG＝第三者データ非流用・NFR-MIN-3）。
 * design-mockups/minimize-icon-proposals.html（案2）準拠。
 *
 * @param direction "down"＝下向きv（縮小操作を示す・展開時に表示）、"up"＝上向き∧（展開操作を示す・縮小時に表示）
 */
function createChevronIcon(direction: "down" | "up"): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("aria-hidden", "true");

  const path = document.createElementNS(SVG_NS, "path");
  // down: M6 9l6 6 6-6（下向きv） / up: M6 15l6-6 6 6（上向き∧）
  path.setAttribute("d", direction === "down" ? "M6 9l6 6 6-6" : "M6 15l6-6 6 6");
  svg.appendChild(path);
  return svg;
}

/**
 * WidgetPanelを構築し、指定のShadow Root配下へ追加する。
 * スタイル（`WIDGET_PANEL_STYLES`）は呼び出し元が自身のCSS文字列に連結して
 * `applyStylesheet`で適用済みであることを前提とする（このファイル自体は`applyStylesheet`を呼ばない）。
 */
export function createWidgetPanel(options: WidgetPanelOptions): WidgetPanelHandle {
  const isCenterModal = options.position === "center-modal";
  const minimizable = options.minimizable === true;
  // 効果分析ログ（BR-AN-14）: onMinimize をローカルへ退避する。showLoginGate の第3引数が
  // `options: LoginGateOptions` で createWidgetPanel の options（WidgetPanelOptions）を
  // シャドーイングするため、ゲート「閉じる」ハンドラ内から options.onMinimize を直接参照できない。
  const onMinimize = options.onMinimize;
  const root = createElement("div", {
    className: isCenterModal ? "spica-widget-backdrop" : "spica-widget-bottom-left",
  });

  const panel = createElement("div", {
    className: "spica-widget-panel",
    attrs: { role: "dialog", "aria-modal": "true", "aria-label": options.ariaLabel },
  });
  root.appendChild(panel);

  const header = createElement("div", { className: "spica-widget-header" });

  // ヘッダー右のボタン。
  // - minimizable=true: 縮小トグル（v ⇄ ∧）。「×＝閉じる」は表示しない（完全閉じる廃止）。
  // - minimizable=false（デフォルト、キャンセルモーダル等）: 従来の「×＝閉じる」。
  let closeButton: HTMLButtonElement | null = null;
  let minimizeToggle: HTMLButtonElement | null = null;
  if (minimizable) {
    minimizeToggle = createElement("button", {
      className: "spica-widget-minimize-toggle",
      attrs: { type: "button", "aria-label": "縮小", "aria-expanded": "true" },
    });
    const collapseIconWrap = createElement("span", { className: "spica-widget-toggle-collapse-icon" });
    collapseIconWrap.appendChild(createChevronIcon("down"));
    const expandIconWrap = createElement("span", { className: "spica-widget-toggle-expand-icon" });
    expandIconWrap.appendChild(createChevronIcon("up"));
    minimizeToggle.appendChild(collapseIconWrap);
    minimizeToggle.appendChild(expandIconWrap);
    header.appendChild(minimizeToggle);
  } else {
    closeButton = createElement("button", {
      className: "spica-widget-close",
      text: "×",
      attrs: { type: "button", "aria-label": "閉じる" },
    });
    header.appendChild(closeButton);
  }

  const lead = createElement("p", { className: "spica-widget-lead", text: options.lead });
  header.appendChild(lead);
  panel.appendChild(header);

  const content = createElement("div", { className: "spica-widget-content" });
  panel.appendChild(content);

  let loginGate: HTMLElement | null = null;
  // 縮小トグルが登録したイベントリスナの解放処理。縮小トグル未構築時はno-op。
  let cleanupMinimize: () => void = () => {};

  // 縮小トグル機構の操作（minimizable=true 時に setupMinimizeToggle の実体へ差し替わる。
  // 未構築時＝minimizable=false はno-op）。showLoginGate「閉じる」ハンドラから参照するため、
  // ハンドラ定義より手前でここに宣言しておく（宣言順の暗黙依存を避ける。実体差し替えは後段）。
  let collapse: () => void = () => {};
  let expand: () => void = () => {};
  let isMinimized = (): boolean => false;

  const remove = (): void => {
    cleanupMinimize();
    root.remove();
  };

  // minimizable=false（デフォルト）: 従来の「×＝閉じる」導線。
  // 効果分析ログ（BR-AN-14）: minimizable=false の×閉じるは分析対象外（縮小＝離脱意思のみ
  // WIDGET_MINIMIZE として計上する）。×閉じるが残るのは確認・キャンセルモーダル等の
  // ファネル外画面のため、ここでは発火しない。
  if (closeButton) {
    closeButton.addEventListener("click", () => {
      if (options.onClose) {
        options.onClose();
      } else {
        remove();
      }
    });
  }

  const showLoginGate = (loginUrl: string, onCancel?: () => void, options?: LoginGateOptions): void => {
    content.classList.add("is-login-gated");
    if (loginGate) loginGate.remove();

    loginGate = createElement("div", {
      className: "spica-widget-login-gate",
      attrs: { role: "dialog", "aria-modal": "true", "aria-label": "ログイン誘導" },
    });
    const card = createElement("div", { className: "spica-widget-login-gate-card" });
    const message = createElement("p", { className: "spica-widget-login-gate-message" });
    const messageText = options?.message ?? LOGIN_GATE_MESSAGE;
    messageText.split("\n").forEach((line, index) => {
      if (index > 0) message.appendChild(createElement("br"));
      message.appendChild(document.createTextNode(line));
    });
    const buttonLabel = options?.buttonLabel ?? "ログイン / 会員登録する";
    const proceedLink = createElement("a", {
      className: "spica-widget-login-gate-proceed",
      text: buttonLabel,
      attrs: { href: loginUrl },
    });
    const cancelButton = createElement("button", {
      className: "spica-widget-login-gate-cancel",
      text: "閉じる",
      attrs: { type: "button" },
    });
    cancelButton.addEventListener("click", () => {
      // ログインゲート「閉じる」の挙動（2026-10-02 縮小対応漏れ修正、BR-U6-14）:
      // - minimizable=true（本体ウィジェット＝アンケート/アポ打診/複数CV）: 縮小トグル化（PR#58、×＝完全閉じる廃止）
      //   の思想と一貫させるため、ウィジェットを完全削除せず縮小する（collapse は冒頭で宣言済みの変数を
      //   参照し、minimizable=true では setupMinimizeToggle が実体へ差し替える）。縮小＝離脱意思として onMinimize
      //   （WIDGET_MINIMIZE、BR-AN-14）を発火する（縮小トグル押下と同じ経路）。呼び出し元の onCancel
      //   （host.remove 等）は無視する（minimizable=true では完全閉じる導線を持たない）。
      //
      //   第2弾修正（2026-10-02、BR-U6-1 整合）: ここで hideLoginGate() を呼ばず、ログインゲート
      //   （白カード＋ブラー is-login-gated）を破棄せず保持したまま縮小する。縮小中はCSS
      //   （.is-minimized .spica-widget-login-gate）で視覚的に隠れ、縮小バーから再展開（is-minimized 解除）
      //   すると未ログインゲートがそのまま復活する（「展開時は従来通りゲートを重畳する」＝BR-U6-1 の秘匿を
      //   往復しても維持）。従来は hideLoginGate() でゲートを破棄してから縮小していたため、再展開で秘匿対象
      //   （アンケート質問・日程候補）が露出していた（第1弾の対応漏れ）。
      // - minimizable=false（確定後表示・キャンセル等のファネル外画面）: 従来どおりゲートを閉じて onCancel を呼ぶ（挙動不変）。
      if (minimizable) {
        collapse();
        onMinimize?.();
      } else {
        hideLoginGate();
        onCancel?.();
      }
    });

    card.appendChild(message);
    card.appendChild(proceedLink);
    card.appendChild(cancelButton);
    loginGate.appendChild(card);
    panel.appendChild(loginGate);
  };

  const hideLoginGate = (): void => {
    content.classList.remove("is-login-gated");
    if (loginGate) {
      loginGate.remove();
      loginGate = null;
    }
  };

  // 縮小トグル機構（2026-09-28、×＝閉じるを廃止し縮小トグルへ）。
  // minimizable=true の場合のみ構築し、冒頭で宣言した collapse/expand/isMinimized を実体へ差し替える
  // （デフォルト＝キャンセルモーダル等は従来の×閉じるで挙動不変・no-op のまま）。
  // bottom-left系・center-modal系の両方に対応する。縮小はアイコン押下時のみ（リサイズ自動縮小は廃止）。
  if (minimizable && minimizeToggle) {
    // 初期状態の判定（1回のみ）:
    // - bottom-left系: スマホ幅（≤767px）なら縮小、PC幅なら展開。
    // - center-modal系: 常に展開（カタログDL完了・お問い合わせ完了）。
    const initiallyMinimized =
      !isCenterModal &&
      typeof window.matchMedia === "function" &&
      window.matchMedia(MOBILE_MEDIA_QUERY).matches;

    // 効果分析ログ（BR-AN-14）: ユーザー操作で縮小トグルを畳んだ時を「離脱意思」として
    // WIDGET_MINIMIZE を発火する。初期表示の縮小（initiallyMinimized）では発火しない
    // （ユーザーの離脱操作ではないため）。発火内容（sessionId/companyRootId 等の付与）は
    // 呼び出し元が渡す onMinimize に委譲する（createWidgetPanel は分析文脈を持たない＝副作用分離）。
    const toggle = setupMinimizeToggle(
      root, header, minimizeToggle, initiallyMinimized, options.onMinimize
    );
    collapse = toggle.collapse;
    expand = toggle.expand;
    isMinimized = toggle.isMinimized;
    cleanupMinimize = toggle.cleanup;
  }

  return { root, content, remove, showLoginGate, hideLoginGate, collapse, expand, isMinimized };
}

/**
 * ウィジェットに縮小トグル機構を組み込む（2026-09-28、×＝閉じるを廃止し縮小トグルへ）。
 *
 * <p>展開⇄縮小を状態クラス `is-minimized`（`root` に付与）のトグルで切り替える。
 * 縮小はヘッダーの縮小トグル（v）押下時のみ発生し（リサイズ連動の自動縮小は行わない）、
 * 展開は縮小バー（ヘッダー）全体のクリック／Enter／Space、または縮小トグル（∧）押下で行う。
 * 縮小トグルのクリックはバー展開へ伝播させない（`stopPropagation`）。
 *
 * <p>縮小時はCSS（`is-minimized`）でコンテンツを隠しヘッダー（タイトル）のみ残す（縦のみ縮小・幅は展開時と同じ）。
 * center-modal系は縮小時に背景暗転を解除し左下へ移動する（CSS側で制御。背後 pass-through・パネルのみ操作可能）。
 *
 * @param root              ルート要素（`is-minimized` のトグル対象）
 * @param header            ヘッダー要素（縮小バーとして機能。クリック/キー操作で展開）
 * @param toggleButton      縮小トグルボタン（v/∧、`aria-label`/`aria-expanded` を更新）
 * @param initiallyMinimized 初期状態を縮小にするか
 * @return collapse/expand/isMinimized とクリーンアップ関数
 */
function setupMinimizeToggle(
  root: HTMLElement,
  header: HTMLElement,
  toggleButton: HTMLButtonElement,
  initiallyMinimized: boolean,
  // 効果分析ログ（BR-AN-14）: ユーザーが縮小トグルで畳んだ時のみ呼ばれる（初期縮小・外部からの
  // collapse() 呼び出しでは呼ばれない）。WIDGET_MINIMIZE の発火に用いる（任意）。
  onUserCollapse?: () => void
): { collapse: () => void; expand: () => void; isMinimized: () => boolean; cleanup: () => void } {
  const collapse = (): void => {
    root.classList.add("is-minimized");
    toggleButton.setAttribute("aria-label", "開く");
    toggleButton.setAttribute("aria-expanded", "false");
    // 縮小時のみヘッダー（縮小バー）を展開操作のためのボタンにする。
    header.setAttribute("role", "button");
    header.setAttribute("tabindex", "0");
    header.setAttribute("aria-label", "開く");
  };
  const expand = (): void => {
    root.classList.remove("is-minimized");
    toggleButton.setAttribute("aria-label", "縮小");
    toggleButton.setAttribute("aria-expanded", "true");
    // 展開時はヘッダーを通常の見出し領域へ戻す（ボタンとして読み上げさせない）。
    header.removeAttribute("role");
    header.removeAttribute("tabindex");
    header.removeAttribute("aria-label");
  };
  const isMinimized = (): boolean => root.classList.contains("is-minimized");

  // 縮小トグルボタン: 展開時=縮小、縮小時=展開（バー展開へは伝播させない）。
  const onToggleClick = (event: MouseEvent): void => {
    event.stopPropagation();
    if (isMinimized()) {
      expand();
    } else {
      collapse();
      // 効果分析ログ（BR-AN-14）: ユーザーの明示的な縮小操作＝離脱意思として通知する。
      onUserCollapse?.();
    }
  };
  // 縮小バー（ヘッダー）全体クリック: 縮小状態のときのみ展開する。
  const onHeaderClick = (): void => {
    if (isMinimized()) expand();
  };
  // キーボード: 縮小状態のとき Enter／Space で展開。
  const onHeaderKeydown = (event: KeyboardEvent): void => {
    if ((event.key === "Enter" || event.key === " ") && isMinimized()) {
      event.preventDefault();
      expand();
    }
  };

  toggleButton.addEventListener("click", onToggleClick);
  header.addEventListener("click", onHeaderClick);
  header.addEventListener("keydown", onHeaderKeydown);

  // 初期状態を適用。
  if (initiallyMinimized) {
    collapse();
  } else {
    expand();
  }

  // 破棄時のクリーンアップ（冪等）。
  let cleaned = false;
  const cleanup = (): void => {
    if (cleaned) return;
    cleaned = true;
    toggleButton.removeEventListener("click", onToggleClick);
    header.removeEventListener("click", onHeaderClick);
    header.removeEventListener("keydown", onHeaderKeydown);
  };

  return { collapse, expand, isMinimized, cleanup };
}

/**
 * promptVisitorLoginと同じロジックでログインURLを組み立てる（loginPromptOverlayUi.tsの実装を再利用）。
 *
 * <p>効果分析ログ（BR-AN-5）: ログイン誘導は全経路（アンケート/アポ打診/複数CV/キャンセル）が本関数で
 * ログインURLを組むため、ここを LOGIN_PROMPT（イベントE＝最優先）の集約発火点とする。reason を
 * 分類軸（loginPromptReason）として載せる。「離脱」自体は発火せず、同一 sessionId の後続有無で
 * 分析側が推定する（BR-AN-4/5）。送信は fire-and-forget（失敗してもログイン導線を阻害しない）。
 *
 * <p><b>2026-09-29 レビュー反映（テナント境界Blocker）</b>: companyRootId 等の分析文脈は呼び出し元
 * （各CVの result/options）しか持たないため、任意の第2引数 `analyticsCtx` で受け取り LOGIN_PROMPT へ
 * 合成する。呼び出し元が渡さない場合は companyRootId 等が欠落し、パイプラインの必須検証
 * （REQUIRED_FIELDS）で当該イベントが保存されない点に注意（呼び出し元は可能な限り渡すこと）。
 */
export function buildWidgetLoginUrl(reason: LoginPromptReason, analyticsCtx: PageEventPayload = {}): string {
  trackLoginPrompt(reason, analyticsCtx);
  return buildLoginRedirectUrl(window.location.href, reason);
}

/**
 * 新規会員登録画面のURLを組み立てる（I261-77 仕様変更）。登録後の戻り先は現在ページ、`reason` はログイン導線と同じ値。
 * ログイン誘導の記録（LOGIN_PROMPT）は {@link buildWidgetLoginUrl} 側で発火済みのため、ここでは発火しない。
 */
export function buildWidgetRegisterUrl(reason: LoginPromptReason): string {
  return buildRegisterRedirectUrl(window.location.href, reason);
}

/**
 * 単一CV系（PreBookingForm/SurveyPopup）のリード文を画面種別・コンテンツ種別に応じて組み立てる
 * （design-mockups/*.html準拠、2026-08-19デザイン更新）。複数CV系（カタログDL完了）は
 * 固定文言のため対象外（呼び出し元で直接指定する）。
 */
export function buildSingleCvLeadText(screenType: string, objectType: string): string {
  switch (screenType) {
    case "INQUIRY_COMPLETE":
      return "お問い合わせした企業に直接話を聞いてみませんか？";
    case "EBOOK_VIEWER":
      return "この資料の掲載企業に直接話を聞いてみませんか？";
    case "DETAIL_PAGE":
      return objectType === "COMPANY"
        ? "この企業に直接話を聞いてみませんか？"
        : "この製品の掲載企業に直接話を聞いてみませんか？";
    default:
      return "直接話を聞いてみませんか？";
  }
}

// re-export（呼び出し元がトークンを直接使いたい場合の便宜のため）
export { BRAND_PRIMARY };

/**
 * 単一CV系の画面種別に応じたWidgetPanelの配置バリアントを返す
 * （design-mockups/*.html準拠）。
 *
 * <p>お問い合わせ完了（INQUIRY_COMPLETE）はモック（inquiry-complete-tag-proposal.html）が
 * 画面中央モーダル（背景暗転）のため center-modal とする。その他の単一CV系
 * （製品詳細・企業詳細・電子ブック閲覧）は左下固定パネル（bottom-left）。
 *
 * @param screenType 画面種別
 * @return 配置バリアント
 */
export function resolveSingleCvPanelPosition(screenType: string): WidgetPanelPosition {
  return screenType === "INQUIRY_COMPLETE" ? "center-modal" : "bottom-left";
}
